import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { User } from 'oidc-client-ts';
import { useEffect } from 'react';
import { Logo } from '@/components/brand/logo';
import { auth } from '@/lib/auth';

export const Route = createFileRoute('/auth/callback')({ component: Callback });

// The code and state are single-use, and this route can mount again or re-run after the URL has
// already moved on, so every caller shares the one exchange this page load makes. A new sign-in
// is a full-page redirect, which resets it.
let exchange: Promise<User | null> | undefined;

function completeSignIn(): Promise<User | null> {
  exchange ??= auth.manager!.signinCallback().then(
    (user) => user ?? null,
    // A stale or replayed sign-in page has no matching state here; start a fresh sign-in
    // rather than landing in the shell without a token.
    async (err: unknown) => {
      console.warn('Sign-in callback failed; starting again', err);
      await auth.manager?.removeUser();
      await auth.signIn('/dashboard');
      return null;
    },
  );
  return exchange;
}

function Callback() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!auth.manager) {
      void navigate({ to: '/dashboard' });
      return;
    }
    void completeSignIn().then((user) => {
      if (user) void navigate({ to: (user.state as string | undefined) ?? '/dashboard' });
    });
  }, [navigate]);
  return (
    <div className="grid min-h-screen place-items-center p-6">
      <div className="flex flex-col items-center gap-3">
        <Logo />
        <p className="text-sm text-muted-foreground">Signing you in…</p>
      </div>
    </div>
  );
}
